/* eslint-disable react/react-in-jsx-scope */
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { getRememberedDocumentComment } from "@/documents/utils/documentComments";
import { CalendarDays, FileText, FolderKanban, MessageSquare, UserRound } from "lucide-react";
import type { ReactNode } from "react";

type DocumentDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pj: any;
  treeFinancer?: any[];
};

const formatDate = (
  value?: string | { date?: string } | null,
  precision?: string | null,
) => {
  const rawDate = typeof value === "string" ? value : value?.date;
  if (!rawDate) return "Non renseigné";

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "Non renseigné";

  return parsed.toLocaleDateString(
    "fr-FR",
    precision === "month"
      ? {
          month: "long",
          year: "numeric",
        }
      : {
          day: "2-digit",
          month: "long",
          year: "numeric",
        },
  );
};

const getExtension = (name?: string | null) => {
  const extension = name?.split(".").pop()?.toUpperCase();
  return extension && extension !== name?.toUpperCase() ? extension : "Non renseigné";
};

const getDisplayName = (value?: string | null) =>
  value?.trim() || "Non renseigné";

const getDocumentComment = (pj: any) => {
  const candidates = [
    pj?.description,
    pj?.comment,
    pj?.commentaire,
    pj?.media?.description,
    pj?.mediaDescription?.description,
    getRememberedDocumentComment(pj),
  ];

  const comment = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  return comment?.trim() || "Aucun commentaire renseigné.";
};

const getProjectContext = (pj: any, treeFinancer?: any[]) => {
  const carrier = treeFinancer?.find((item) => Number(item?.id) === Number(pj?.pp));
  const program = carrier?.programs?.find((item: any) => Number(item?.id) === Number(pj?.program));
  const project = program?.projects?.find((item: any) => Number(item?.id) === Number(pj?.project));

  return {
    carrierName: carrier?.name ?? "Non renseigné",
    programName: program?.name ?? "Non renseigné",
    projectName: project?.name ?? "Non renseigné",
  };
};

const DetailCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value?: string | number | null;
}) => (
  <div className="flex min-w-0 gap-3 rounded-md border border-[#dbe6ef] bg-white px-4 py-3 shadow-sm">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#eef4e8] text-[#1d5215]">
      {icon}
    </div>
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5f7180]">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-medium text-[#1f2937]">
        {value || "Non renseigné"}
      </dd>
    </div>
  </div>
);

const FileBadge = ({ extension }: { extension: string }) => (
  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-[#1d5215] text-white shadow-sm">
    <FileText className="h-7 w-7" />
    <span className="sr-only">{extension}</span>
  </div>
);

export const DocumentDetailsContent = ({
  pj,
  treeFinancer,
}: Pick<DocumentDetailsDialogProps, "pj" | "treeFinancer">) => {
  const context = getProjectContext(pj, treeFinancer);
  const extension = getExtension(pj?.name);

  return (
    <>
      <header className="border-b border-[#e5e7eb] bg-[#f8fafc] px-6 py-5 text-left">
        <div className="flex min-w-0 items-start gap-4 pr-8">
          <FileBadge extension={extension} />
          <div className="min-w-0">
            <h2 className="break-words text-xl font-semibold text-[#1f2937]">
              {getDisplayName(pj?.name)}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#5f7180]">
              <span className="rounded-full bg-white px-3 py-1 font-medium text-[#1d5215]">
                {extension}
              </span>
              <span>
                Ajouté {pj?.datePrecision === "month" ? "en" : "le"}{" "}
                {formatDate(pj?.at ?? pj?.createdAt, pj?.datePrecision)}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="space-y-5 px-6 py-5">
        <dl className="grid gap-3">
          <DetailCard
            icon={<UserRound className="h-4 w-4" />}
            label="Porteur"
            value={context.carrierName}
          />
          <DetailCard
            icon={<FolderKanban className="h-4 w-4" />}
            label="Programme"
            value={context.programName}
          />
          <DetailCard
            icon={<FolderKanban className="h-4 w-4" />}
            label="Projet"
            value={context.projectName}
          />
          <DetailCard
            icon={<CalendarDays className="h-4 w-4" />}
            label="Date d’ajout"
            value={formatDate(pj?.at ?? pj?.createdAt, pj?.datePrecision)}
          />
        </dl>

        <div className="rounded-md border border-[#e5e7eb] bg-[#f8fafc] px-4 py-4">
          <div className="mb-2 flex items-center gap-2 text-[#5f7180]">
            <MessageSquare className="h-4 w-4" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em]">
              Commentaire
            </p>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm text-[#1f2937]">
            {getDocumentComment(pj)}
          </p>
        </div>
      </div>
    </>
  );
};

const DocumentDetailsDialog = ({
  open,
  onOpenChange,
  pj,
  treeFinancer,
}: DocumentDetailsDialogProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        onInteractOutside={(event) => event.preventDefault()}
        className="top-[7rem] bottom-4 h-auto w-full overflow-auto rounded-l-md p-0 sm:top-[8.5rem] sm:max-w-[520px]"
      >
        <DocumentDetailsContent pj={pj} treeFinancer={treeFinancer} />
      </SheetContent>
    </Sheet>
  );
};

export default DocumentDetailsDialog;
